import { useState, type FormEvent } from 'react';
import { Plus } from 'lucide-react';

import { Dialog } from '../components/Dialog';
import { KeyReveal } from '../components/KeyCreate';
import { ErrorCallout, InlineConfirm, PageHead, Segmented, SkeletonRows, Spinner } from '../components/ui';
import { track } from '../lib/analytics';
import { api } from '../lib/api';
import { ago, fullTime } from '../lib/format';
import { useAction, useDocumentTitle, useNow, useResource } from '../lib/hooks';
import { useSession } from '../lib/session';
import type { ApiKey, CreatedKey } from '../lib/types';

const KINDS: Record<ApiKey['kind'], { label: string; hint: string }> = {
  personal: { label: 'Personal', hint: 'Yours. It stops working if you leave the team.' },
  service: { label: 'Service', hint: 'The team’s. It works until someone revokes it.' },
};

/** Name a key, and for an owner whose it is. Mounts with the dialog, so it opens empty. */
function KeyForm({ onCreated, onCancel }: { onCreated: (key: CreatedKey) => void; onCancel: () => void }) {
  const { me } = useSession();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ApiKey['kind']>('personal');
  const create = useAction(async (body: { name: string; kind: ApiKey['kind'] }) => api<CreatedKey>('/v1/keys', { method: 'POST', body }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const key = await create.run({ name: trimmed, kind });
    if (key) {
      track('api_key_created');
      onCreated(key);
    }
  }

  return (
    <form className="key-form stack" onSubmit={submit}>
      <div className="field">
        <label htmlFor="key-name">Name</label>
        <input
          id="key-name"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="claude-code, ci, my-laptop"
          maxLength={64}
          autoComplete="off"
          spellCheck={false}
          autoFocus
        />
      </div>
      {me?.role === 'owner' ? (
        <div className="field">
          <span className="label">Kind</span>
          <Segmented
            label="Kind"
            options={[
              { value: 'personal', label: KINDS.personal.label },
              { value: 'service', label: KINDS.service.label },
            ]}
            value={kind}
            onChange={setKind}
          />
          <span className="hint">{KINDS[kind].hint}</span>
        </div>
      ) : null}
      {create.error ? <ErrorCallout error={create.error} /> : null}
      <div className="dialog__actions">
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn--primary" disabled={!name.trim() || create.busy} aria-busy={create.busy}>
          {create.busy ? <Spinner /> : null}
          Create key
        </button>
      </div>
    </form>
  );
}

export function KeysPage() {
  useDocumentTitle('API keys');
  // A key made since the last tick must not read "in <1 min".
  const now = Math.max(useNow(30_000), Date.now());
  const res = useResource<{ keys: ApiKey[] }>('/v1/keys');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const revoke = useAction(async (id: string) => api(`/v1/keys/${encodeURIComponent(id)}`, { method: 'DELETE' }));
  const keys = res.data?.keys ?? [];
  const closeDialog = () => {
    setCreating(false);
    setCreated(null);
  };

  return (
    <>
      <PageHead
        title="API keys"
        actions={
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            Create key
          </button>
        }
      />
      <div className="sheet">
        {res.error ? <ErrorCallout error={res.error} onRetry={() => void res.reload()} /> : null}
        {revoke.error ? <ErrorCallout error={revoke.error} /> : null}
        <div className="surface">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Key</th>
                  <th scope="col">Kind</th>
                  <th scope="col" className="w2-hide">
                    Created by
                  </th>
                  <th scope="col">Created</th>
                  <th scope="col">Last used</th>
                  <th scope="col" className="r">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {res.loading && !res.data ? (
                  <SkeletonRows cols={7} rows={3} />
                ) : keys.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="m-full table-empty">
                      No keys yet.{' '}
                      <button type="button" className="link-btn" onClick={() => setCreating(true)}>
                        Create one
                      </button>{' '}
                      for each agent or machine that calls the API.
                    </td>
                  </tr>
                ) : (
                  keys.map((k) => (
                    <tr key={k.id}>
                      <td className="m-title" style={{ maxWidth: 240 }}>
                        <span className="cell-trunc key-name" title={k.name}>
                          {k.name}
                        </span>
                      </td>
                      <td className="m-meta mono small nowrap">{k.mask}</td>
                      <td className="m-meta m-inline">
                        <span className="tag">{KINDS[k.kind].label}</span>
                      </td>
                      <td className="m-meta w2-hide" style={{ maxWidth: 220 }}>
                        <span className="cell-trunc" title={k.createdBy?.email}>
                          {k.createdBy?.email ?? <span className="muted">—</span>}
                        </span>
                      </td>
                      <td className="m-meta nowrap" data-label="Created" title={fullTime(k.createdAt)}>
                        {ago(k.createdAt, now)}
                      </td>
                      <td className="m-meta nowrap" data-label="Used" title={k.lastUsedAt ? fullTime(k.lastUsedAt) : undefined}>
                        {k.lastUsedAt ? ago(k.lastUsedAt, now) : <span className="muted">Never</span>}
                      </td>
                      <td className="r m-end">
                        <InlineConfirm
                          trigger="Revoke"
                          question={`Revoke ${k.name}?`}
                          confirmLabel="Revoke"
                          busy={revoke.busy && revoking === k.id}
                          onConfirm={async () => {
                            setRevoking(k.id);
                            await revoke.run(k.id);
                            setRevoking(null);
                            await res.reload();
                          }}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Dialog open={creating} onClose={closeDialog} title={created ? `Key “${created.name}” is ready` : 'Create API key'} width={created ? 560 : 460} closeOnBackdrop={!created}>
        {created ? (
          <KeyReveal created={created} onDone={closeDialog} bare />
        ) : (
          <KeyForm
            onCancel={closeDialog}
            onCreated={(key) => {
              setCreated(key);
              void res.reload();
            }}
          />
        )}
      </Dialog>
    </>
  );
}
